import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { customerApi } from '../services/customerApi';
import { useNotification } from '../components/Notification';
import ColorSelector from '../components/ColorSelector';
import Spinner from '../components/Spinner';
import './Cars.css';

function CustomerTagSelector({ tenantId, selectedTags = [], onChange }) {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTags();
  }, [tenantId]);

  const loadTags = async () => {
    try {
      const data = await customerApi.get('/tags');
      setTags(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading tags:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTagToggle = (tagId) => {
    if (selectedTags.includes(tagId)) {
      onChange(selectedTags.filter(id => id !== tagId));
    } else {
      onChange([...selectedTags, tagId]);
    }
  };

  if (loading) {
    return <div>Lade Tags...</div>;
  }

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
      {tags.map(tag => (
        <label
          key={tag.id}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem 1rem',
            border: `2px solid ${selectedTags.includes(tag.id) ? tag.color : '#ddd'}`,
            borderRadius: '20px',
            backgroundColor: selectedTags.includes(tag.id) ? tag.color : '#fff',
            cursor: 'pointer',
            fontSize: '0.9rem'
          }}
        >
          <input
            type="checkbox"
            checked={selectedTags.includes(tag.id)}
            onChange={() => handleTagToggle(tag.id)}
            style={{ display: 'none' }}
          />
          <span style={{ color: selectedTags.includes(tag.id) ? '#000' : '#333' }}>
            {tag.name}
          </span>
        </label>
      ))}
      {tags.length === 0 && <span style={{ color: '#666' }}>Keine Tags verfügbar</span>}
    </div>
  );
}

function CustomerEquipmentSelector({ tenantId, selectedEquipment = [], onChange, make }) {
  const { showNotification } = useNotification();
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEquipmentName, setNewEquipmentName] = useState('');
  const [newEquipmentColor, setNewEquipmentColor] = useState('#4ECDC4');

  useEffect(() => {
    loadEquipment();
  }, [tenantId, make]);

  const loadEquipment = async () => {
    try {
      setLoading(true);
      const params = make ? `?make=${encodeURIComponent(make)}` : '';
      const data = await customerApi.get(`/equipment${params}`);
      setEquipment(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading equipment:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEquipmentToggle = (equipmentId) => {
    if (selectedEquipment.includes(equipmentId)) {
      onChange(selectedEquipment.filter(id => id !== equipmentId));
    } else {
      onChange([...selectedEquipment, equipmentId]);
    }
  };

  const handleAddEquipment = async (e) => {
    e.preventDefault();
    if (!newEquipmentName.trim()) {
      return;
    }
    try {
      const newEquipment = await customerApi.post('/equipment', {
        name: newEquipmentName.trim(),
        make: make || null,
        color: newEquipmentColor
      });
      setEquipment([...equipment, newEquipment]);
      setNewEquipmentName('');
      setNewEquipmentColor('#4ECDC4');
      const newSelected = [...selectedEquipment, newEquipment.id];
      onChange(newSelected);
    } catch (error) {
      console.error('Error creating equipment:', error);
      showNotification(error.response?.data?.error || error.message || 'Fehler beim Erstellen der Sonderaustattung', 'error');
    }
  };

  const presetColors = [
    '#4ECDC4', '#45B7D1', '#FFA07A', '#FFD93D', '#C77DFF',
    '#6BCF7F', '#FF8C94', '#9B8DFF', '#FFB84D', '#FF6B6B'
  ];

  if (loading) {
    return <div>Lade Ausstattung...</div>;
  }

  const selectedEquipmentObjects = equipment.filter(eq => selectedEquipment.includes(eq.id));
  const availableEquipment = equipment.filter(eq => !selectedEquipment.includes(eq.id));

  return (
    <div>
      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Sonderaustattungen</label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.5rem' }}>
        {selectedEquipmentObjects.map(eq => (
          <span
            key={eq.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              border: `2px solid ${eq.color}`,
              borderRadius: '20px',
              backgroundColor: eq.color,
              fontSize: '0.9rem'
            }}
          >
            <span style={{ color: '#000' }}>{eq.name}</span>
            <span
              onClick={() => handleEquipmentToggle(eq.id)}
              style={{ cursor: 'pointer', fontSize: '1.2rem' }}
            >
              ×
            </span>
          </span>
        ))}
      </div>
      
      <form onSubmit={handleAddEquipment} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem' }}>
        <input
          type="text"
          value={newEquipmentName}
          onChange={(e) => setNewEquipmentName(e.target.value)}
          placeholder="Sonderaustattung hinzufügen..."
          style={{
            flex: 1,
            padding: '0.5rem',
            border: '1px solid #ddd',
            borderRadius: '4px'
          }}
        />
        <input
          type="color"
          value={newEquipmentColor}
          onChange={(e) => setNewEquipmentColor(e.target.value)}
          style={{
            width: '40px',
            height: '40px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
          title="Farbe wählen"
        />
        <button type="submit" className="btn-small" disabled={!newEquipmentName.trim()}>
          +
        </button>
      </form>
      
      {availableEquipment.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
          {availableEquipment.map(eq => (
            <label
              key={eq.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                border: `2px solid #ddd`,
                borderRadius: '20px',
                backgroundColor: '#fff',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              <input
                type="checkbox"
                checked={false}
                onChange={() => handleEquipmentToggle(eq.id)}
                style={{ display: 'none' }}
              />
              <span style={{ color: '#333' }}>{eq.name}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function CustomerEditCar() {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { id } = useParams();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tenantId, setTenantId] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedEquipment, setSelectedEquipment] = useState([]);
  const [knownDefects, setKnownDefects] = useState([]);
  const [accidents, setAccidents] = useState([]);
  const [formData, setFormData] = useState({
    make: '',
    model: '',
    year: '',
    firstRegistration: '',
    licensePlate: '',
    vin: '',
    mileage: 0,
    fuelType: '',
    transmission: '',
    color: '',
    engineCode: '',
    hsn: '',
    tsn: '',
    typenschein: '',
    reminderDate: '',
    reminderNote: '',
    accidentVehicle: false
  });

  useEffect(() => {
    const token = localStorage.getItem('customerToken');
    const storedCustomer = localStorage.getItem('customer');
    if (!token || !storedCustomer) {
      navigate('/customer-login');
      return;
    }
    
    try {
      const customer = JSON.parse(storedCustomer);
      setTenantId(customer.tenantId || null);
    } catch (e) {
      console.error('Error parsing customer:', e);
      setTenantId(null);
    }
    
    if (id && id !== 'new') {
      loadCar();
    } else {
      setLoading(false);
    }
  }, [id]);


  const loadCar = async () => {
    try {
      setLoading(true);
      const data = await customerApi.get(`/customer-cars/${id}`);
      setCar(data);
      const reminderDate = data.reminderDate ? (data.reminderDate.includes('T') ? data.reminderDate.split('T')[0] : data.reminderDate) : '';
      setFormData({
        make: data.make || '',
        model: data.model || '',
        year: data.year || '',
        firstRegistration: data.firstRegistration ? (data.firstRegistration.includes('T') ? data.firstRegistration.split('T')[0] : data.firstRegistration) : '',
        licensePlate: data.licensePlate || '',
        vin: data.vin || '',
        mileage: data.mileage || 0,
        fuelType: data.fuelType || '',
        transmission: data.transmission || '',
        color: data.color || '',
        engineCode: data.engineCode || '',
        hsn: data.hsn || '',
        tsn: data.tsn || '',
        typenschein: data.typenschein || '',
        reminderDate: reminderDate,
        reminderNote: data.reminderNote || '',
        accidentVehicle: data.accidentVehicle || false
      });
      setSelectedTags(data.tags || []);
      setSelectedEquipment(data.equipment || []);
      setKnownDefects(data.knownDefects || []);
      setAccidents(data.accidents || []);
    } catch (error) {
      console.error('Error loading car:', error);
      showNotification('Fehler beim Laden des Autos', 'error');
      navigate('/customer-dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        year: formData.year ? parseInt(formData.year) : null,
        mileage: formData.mileage ? parseInt(formData.mileage) : 0,
        firstRegistration: formData.firstRegistration ? new Date(formData.firstRegistration).toISOString() : null,
        reminderDate: formData.reminderDate ? new Date(formData.reminderDate).toISOString() : null,
        reminderNote: formData.reminderNote || null,
        tags: selectedTags,
        equipment: selectedEquipment,
        knownDefects: knownDefects,
        accidents: accidents
      };
      
      if (id && id !== 'new') {
        await customerApi.put(`/customer-cars/${id}`, submitData);
        showNotification('Auto erfolgreich aktualisiert', 'success');
      } else {
        await customerApi.post('/customer-cars', submitData);
        showNotification('Auto erfolgreich erstellt', 'success');
      }
      navigate('/customer-dashboard');
    } catch (error) {
      console.error('Error saving car:', error);
      const errorMessage = error.response?.data?.error || 'Fehler beim Speichern des Autos';
      showNotification(errorMessage, 'error');
    }
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className="customer-portal" style={{ padding: '2rem' }}>
      <div className="page-header">
        <h1>{id && id !== 'new' ? 'Auto bearbeiten' : 'Neues Auto'}</h1>
        <button type="button" onClick={() => navigate('/customer-dashboard')} className="btn-secondary">
          Zurück
        </button>
      </div>

      <div className="form-container">
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h2>Fahrzeugdaten</h2>
            
            <div className="form-row">
              <div className="form-group">
                <label>Marke *</label>
                <input
                  type="text"
                  value={formData.make}
                  onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Modell *</label>
                <input
                  type="text"
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Baujahr</label>
                <input
                  type="number"
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: e.target.value === '' ? '' : e.target.value })}
                  min="1900"
                  max={new Date().getFullYear() + 1}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Erstzulassung</label>
                <input
                  type="date"
                  value={formData.firstRegistration}
                  onChange={(e) => setFormData({ ...formData, firstRegistration: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Kennzeichen</label>
                <input
                  type="text"
                  value={formData.licensePlate}
                  onChange={(e) => setFormData({ ...formData, licensePlate: e.target.value.toUpperCase() })}
                />
              </div>
              <div className="form-group">
                <label>FIN/VIN</label>
                <input
                  type="text"
                  value={formData.vin}
                  onChange={(e) => setFormData({ ...formData, vin: e.target.value.toUpperCase() })}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Kilometerstand</label>
                <input
                  type="number"
                  value={formData.mileage}
                  onChange={(e) => setFormData({ ...formData, mileage: e.target.value === '' ? '' : e.target.value })}
                  onBlur={(e) => {
                    const val = e.target.value === '' ? 0 : parseInt(e.target.value) || 0;
                    setFormData({ ...formData, mileage: val });
                  }}
                  min="0"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Kraftstoffart</label>
                <select
                  value={formData.fuelType || ''}
                  onChange={(e) => setFormData({ ...formData, fuelType: e.target.value })}
                >
                  <option value="">Bitte wählen</option>
                  <option value="BENZIN">Benzin</option>
                  <option value="DIESEL">Diesel</option>
                  <option value="ELEKTRO">Elektro</option>
                  <option value="HYBRID">Hybrid</option>
                  <option value="LPG">LPG</option>
                  <option value="CNG">CNG/Erdgas</option>
                </select>
              </div>
              <div className="form-group">
                <label>Getriebeart</label>
                <select
                  value={formData.transmission || ''}
                  onChange={(e) => setFormData({ ...formData, transmission: e.target.value })}
                >
                  <option value="">Bitte wählen</option>
                  <option value="MANUAL">Manuell</option>
                  <option value="AUTOMATIC">Automatik</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <ColorSelector
                value={formData.color || ''}
                onChange={(color) => setFormData({ ...formData, color })}
                label="Farbe"
              />
            </div>

            <div className="form-group">
              <label>Motorkennbuchstabe</label>
              <input
                type="text"
                value={formData.engineCode || ''}
                onChange={(e) => setFormData({ ...formData, engineCode: e.target.value.toUpperCase() })}
                placeholder="z.B. ANA"
                maxLength="4"
              />
            </div>

            {tenantId ? (
              <div className="form-group">
                <label>Typenschein</label>
                <input
                  type="text"
                  value={formData.typenschein || ''}
                  onChange={(e) => setFormData({ ...formData, typenschein: e.target.value })}
                  placeholder="Typenschein-Nummer"
                />
              </div>
            ) : (
              <div className="form-row">
                <div className="form-group">
                  <label>HSN (Herstellerschlüsselnummer)</label>
                  <input
                    type="text"
                    value={formData.hsn || ''}
                    onChange={(e) => setFormData({ ...formData, hsn: e.target.value.toUpperCase() })}
                    placeholder="z.B. 0001"
                    maxLength="4"
                  />
                </div>
                <div className="form-group">
                  <label>TSN (Typschlüsselnummer)</label>
                  <input
                    type="text"
                    value={formData.tsn || ''}
                    onChange={(e) => setFormData({ ...formData, tsn: e.target.value.toUpperCase() })}
                    placeholder="z.B. ABC"
                  />
                </div>
              </div>
            )}

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.accidentVehicle || false}
                  onChange={(e) => setFormData({ ...formData, accidentVehicle: e.target.checked })}
                />
                <span>Unfallfahrzeug</span>
              </label>
            </div>
          </div>

          <div className="form-section">
            <h2>Weitere Informationen</h2>
            
            <div className="form-group">
              <label>Erinnerungstermin (optional)</label>
              <input
                type="date"
                value={formData.reminderDate ? formData.reminderDate.split('T')[0] : ''}
                onChange={(e) => setFormData({ ...formData, reminderDate: e.target.value ? new Date(e.target.value).toISOString() : '' })}
              />
            </div>

            {formData.reminderDate && (
              <div className="form-group">
                <label>Erinnerungstext (optional)</label>
                <textarea
                  value={formData.reminderNote || ''}
                  onChange={(e) => setFormData({ ...formData, reminderNote: e.target.value })}
                  placeholder="Was soll in Erinnerung gerufen werden?"
                  rows={3}
                />
              </div>
            )}

            <div className="form-group">
              <label>Tags</label>
              <CustomerTagSelector
                tenantId={tenantId}
                selectedTags={selectedTags}
                onChange={setSelectedTags}
              />
            </div>

            <div className="form-group">
              <CustomerEquipmentSelector
                tenantId={tenantId}
                selectedEquipment={selectedEquipment}
                onChange={setSelectedEquipment}
                make={formData.make}
              />
            </div>
          </div>

          <div className="form-section">
            <h2>Bekannte Mängel</h2>
            {knownDefects.map((defect, index) => (
              <div key={defect.id || index} style={{ marginBottom: '1rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '4px' }}>
                <div className="form-row">
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Titel</label>
                    <input
                      type="text"
                      value={defect.title || ''}
                      onChange={(e) => {
                        const updated = [...knownDefects];
                        updated[index] = { ...updated[index], title: e.target.value };
                        setKnownDefects(updated);
                      }}
                    />
                  </div>
                  <div className="form-group">
                    <label>Datum</label>
                    <input
                      type="date"
                      value={defect.date ? defect.date.split('T')[0] : ''}
                      onChange={(e) => {
                        const updated = [...knownDefects];
                        updated[index] = { ...updated[index], date: e.target.value ? new Date(e.target.value).toISOString() : '' };
                        setKnownDefects(updated);
                      }}
                    />
                  </div>
                  <div className="form-group">
                    <label>Kilometerstand</label>
                    <input
                      type="number"
                      value={defect.mileage || ''}
                      onChange={(e) => {
                        const updated = [...knownDefects];
                        updated[index] = { ...updated[index], mileage: e.target.value ? parseInt(e.target.value) : null };
                        setKnownDefects(updated);
                      }}
                    />
                  </div>
                  <div className="form-group">
                    <label>&nbsp;</label>
                    <button
                      type="button"
                      onClick={() => setKnownDefects(knownDefects.filter((_, i) => i !== index))}
                      className="btn-small btn-danger"
                    >
                      Löschen
                    </button>
                  </div>
                </div>
                <div className="form-group">
                  <label>Beschreibung</label>
                  <textarea
                    value={defect.description || ''}
                    onChange={(e) => {
                      const updated = [...knownDefects];
                      updated[index] = { ...updated[index], description: e.target.value };
                      setKnownDefects(updated);
                    }}
                    rows={2}
                  />
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setKnownDefects([...knownDefects, { id: `defect-${Date.now()}`, title: '', description: '', date: '', mileage: null }])}
              className="btn-small"
            >
              + Mangel hinzufügen
            </button>
          </div>

          <div className="form-section">
            <h2>Unfälle/Schäden</h2>
            {accidents.map((accident, index) => (
              <div key={accident.id || index} style={{ marginBottom: '1rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '4px' }}>
                <div className="form-row">
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Titel</label>
                    <input
                      type="text"
                      value={accident.title || ''}
                      onChange={(e) => {
                        const updated = [...accidents];
                        updated[index] = { ...updated[index], title: e.target.value };
                        setAccidents(updated);
                      }}
                    />
                  </div>
                  <div className="form-group">
                    <label>Datum</label>
                    <input
                      type="date"
                      value={accident.date ? accident.date.split('T')[0] : ''}
                      onChange={(e) => {
                        const updated = [...accidents];
                        updated[index] = { ...updated[index], date: e.target.value ? new Date(e.target.value).toISOString() : '' };
                        setAccidents(updated);
                      }}
                    />
                  </div>
                  <div className="form-group">
                    <label>Kilometerstand</label>
                    <input
                      type="number"
                      value={accident.mileage || ''}
                      onChange={(e) => {
                        const updated = [...accidents];
                        updated[index] = { ...updated[index], mileage: e.target.value ? parseInt(e.target.value) : null };
                        setAccidents(updated);
                      }}
                    />
                  </div>
                  <div className="form-group">
                    <label>&nbsp;</label>
                    <button
                      type="button"
                      onClick={() => setAccidents(accidents.filter((_, i) => i !== index))}
                      className="btn-small btn-danger"
                    >
                      Löschen
                    </button>
                  </div>
                </div>
                <div className="form-group">
                  <label>Beschreibung</label>
                  <textarea
                    value={accident.description || ''}
                    onChange={(e) => {
                      const updated = [...accidents];
                      updated[index] = { ...updated[index], description: e.target.value };
                      setAccidents(updated);
                    }}
                    rows={2}
                  />
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setAccidents([...accidents, { id: `accident-${Date.now()}`, title: '', description: '', date: '', mileage: null }])}
              className="btn-small"
            >
              + Unfall/Schaden hinzufügen
            </button>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-primary">
              Speichern
            </button>
            <button
              type="button"
              onClick={() => navigate('/customer-dashboard')}
              className="btn-secondary"
            >
              Abbrechen
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CustomerEditCar;
